import { IconBulb } from '@tabler/icons-react'
import { BreakUpOnboarding, BreakUpQuizOnboarding, HumanteqOnboarding, type OnboardingScenario, type OnboardingVariant } from '../../features/onboarding'
import { InsightsIndexRedirect, ProgressScreen, SelfViewScreen, SpeechScreen, StrengthsScreen, TopicsScreen, INSIGHTS_NAV } from '../../features/insights'
import type { ComponentType } from 'react'
import type { SegmentId } from '../../features/product-segment'
import Chat from '../../screens/Chat/Chat'
import Practices from '../../screens/Practices/Practices'
import Pricing from '../../screens/Pricing/Pricing'
import FAQ from '../../screens/FAQ/FAQ'
import BreakupFAQ from '../../screens/FAQ/BreakupFAQ'
import Settings from '../../screens/Settings/Settings'
import Psychologist from '../../screens/Psychologist/Psychologist'
import Support from '../../screens/Support/Support'
import Install from '../../screens/Install/Install'
import Report from '../../screens/Report/Report'
import { NAV, type NavItem } from '../../components/appShellNav'
import { breakUpTheme, humanteqTheme } from './themes'
import { SosPage } from '../../features/sos'
import { JourneyPage } from '../../features/journey'
import { StartPage } from '../../features/start'
import LegacyNextStepRedirect from './LegacyNextStepRedirect'

export interface ProductSection { path: string; Component: ComponentType; menu?: NavItem }
export interface ProductConfig {
  id: SegmentId
  theme: Record<`--${string}`, string>
  sections: ProductSection[]
  onboarding: OnboardingScenario
}
// «Инсайты о Вас» (spec §2.8): the five design-workspace screens
// (pwa-design@insights, ported 21.10) on GET /engine/insights. The sidebar
// carries the design's collapsible group (IconBulb parent + the five
// children); /insights redirects to the first screen. The mobile tab
// bar lives in the feature. humanteq only — break_up has neither tab nor
// route (decision 21.10: «инсайты не будут в расставании», #4463).
const INSIGHTS_SECTIONS: ProductSection[] = [
  { path: '/insights', Component: InsightsIndexRedirect },
  { path: '/insights/speech', Component: SpeechScreen, menu: { label: 'Инсайты о Вас', Icon: IconBulb, children: INSIGHTS_NAV } },
  { path: '/insights/progress', Component: ProgressScreen },
  { path: '/insights/topics', Component: TopicsScreen },
  { path: '/insights/strengths', Component: StrengthsScreen },
  { path: '/insights/self', Component: SelfViewScreen },
]
const pages: [string, ComponentType][] = [
  ['/', Chat], ['/practices', Practices], ['/psychologist', Psychologist], ['/pricing', Pricing],
  ['/faq', FAQ], ['/install', Install], ['/settings', Settings], ['/support', Support], ['/report', Report],
]
const sections: ProductSection[] = pages.map(([path, Component]) => ({ path, Component, menu: NAV.find((item) => item.to === path) }))
// «Инсайты» идёт сразу после чата — живой раздел, а не справочник
sections.splice(1, 0, ...INSIGHTS_SECTIONS)
// version 3 — §0.2 funnel (name + form of address → style + topics → done);
// the bump drops stale v2 drafts (style-on-step-1 pointers, no address).
const baseline = { theme: humanteqTheme, sections, onboarding: { id: 'humanteq', version: 4, Component: HumanteqOnboarding } }
export const PRODUCT_CONFIG: Record<SegmentId, ProductConfig> = {
  humanteq: { id: 'humanteq', ...baseline },
  break_up: {
    id: 'break_up', ...baseline, theme: breakUpTheme,
    // Практики для break_up выключены — таб и роут не включаются в секции.
    // «Психолог» уходит из меню (решение 16.10: бот-инициированная эскалация
    // остаётся, публичной услуги в каталоге больше нет) — роут сохранён как
    // скрытый. «С чего начать» показывается один раз после онбординга
    // (completionPath), поэтому роут живёт без пункта меню (доска, 16.10).
    // Задания «Следующего шага» живут на «Пути» (доска, 20.10) — /next-step
    // остаётся скрытым редиректом, пункта меню больше нет.
    // «Инсайты о Вас» — только основной продукт (redmine #4463, 21.10:
    // «инсайты не будут в расставании»): ни таба, ни роута; агент памяти
    // их и не собирает — оверлей feature-breakup гасит
    // prompts/memory_insights.txt пустым файлом.
    sections: [{ path: '/start', Component: StartPage }, sections[0], { path: '/progress', Component: JourneyPage, menu: { to: '/progress', label: 'Путь', icon: 'icon-journey.svg' } }, { path: '/next-step', Component: LegacyNextStepRedirect }, { path: '/sos', Component: SosPage, menu: { to: '/sos', label: 'SOS', icon: 'icon-sos.svg' } }, ...sections.slice(1).filter((section) => section.path !== '/practices' && section.path !== '/psychologist' && !section.path.startsWith('/insights')).map(section => section.path === '/faq' ? { ...section, Component: BreakupFAQ } : section)],
    onboarding: { id: 'break_up', version: 1, completionPath: '/start', Component: BreakUpOnboarding },
  },
}
const quizConfig: ProductConfig = {
  ...PRODUCT_CONFIG.break_up,
  onboarding: { ...PRODUCT_CONFIG.break_up.onboarding, variant: 'quiz', Component: BreakUpQuizOnboarding },
}
export function getProductConfig(segment: SegmentId, variant: OnboardingVariant = 'default'): ProductConfig {
  return segment === 'break_up' && variant === 'quiz' ? quizConfig : PRODUCT_CONFIG[segment]
}
export function productNavigation(config: ProductConfig): NavItem[] {
  const items = config.sections.flatMap((section) => section.menu ? [section.menu] : [])
  const divider = items.findIndex((item) => item.utility)
  if (divider >= 0) items.splice(divider, 0, { divider: true })
  return items
}
